import LayoutHeader from "@features/common/header/layoutHeader/LayoutHeader";
import styles from "./Reports.module.scss";
import { useAppSelector } from "@redux/hooks";
import { selectDashboardData } from "@redux/slices/CommonSlice";
import { useEffect, useMemo, useState } from "react";
import { CreditLevelIcon, UserLevelIcon } from "@features/common/SvgIcons";
import { useFetchReportsQuery } from "@features/reports/reportsAPI";
import { CircularProgress } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LevelType, TabType } from "interfaces/common.interface";
import {
  getLevelForTab,
  isValidTabType,
  REPORT_TABS,
  REPORT_TABS_BY_LEVEL,
  TAB_LABELS,
} from "@utils/reportsUtils";

const headerTabs = [
  {
    key: "credit_level",
    label: "Credit Level",
    icon: <CreditLevelIcon />,
  },
  {
    key: "user_level",
    label: "User Level",
    icon: <UserLevelIcon />,
  },
];

const Reports = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Extract query parameters from URL
  const startDate = searchParams.get("start_date");
  const endDate = searchParams.get("end_date");
  const userId = searchParams.get("user_id");
  const country = searchParams.get("country");
  const paramValue = searchParams.get("report_type");
  const initialTab: TabType = isValidTabType(paramValue)
    ? (paramValue as TabType)
    : REPORT_TABS.credit_inissuance;
  const initialLevel = getLevelForTab(initialTab);

  // Track the currently selected report level (credit_level or user_level)
  const [activeLevel, setActiveLevel] = useState<LevelType>(initialLevel);
  // Track the currently selected report tab within the level
  const [activeTab, setActiveTab] = useState<TabType>(initialTab);

  const dashboardData = useAppSelector(selectDashboardData);

  const reportQueryParams = useMemo(() => {
    const params: Record<string, string> = {
      report_type: activeTab, // mandatory
    };

    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (userId) params.user_id = userId;
    if (country) params.country = country;

    return params;
  }, [activeTab, startDate, endDate, userId, country]);

  // Fetch report data based on the active report tab
  const { data, isFetching: reportsLoading } = useFetchReportsQuery(
    reportQueryParams,
    {
      refetchOnMountOrArgChange: true,
    },
  );

  // Ensure URL has valid report_type on initial page load
  useEffect(() => {
    const paramValue = searchParams.get("report_type");

    if (!paramValue || !isValidTabType(paramValue)) {
      const fallbackTab: TabType = REPORT_TABS.credit_inissuance;

      setActiveTab(fallbackTab);
      setActiveLevel(getLevelForTab(fallbackTab));

      navigate(`?report_type=${fallbackTab}`, { replace: true });
    }
  }, []);

  // Update report tab and URL when user selects a different tab
  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    navigate(`?report_type=${tab}`, { replace: true });
  };

  // Switch between Credit Level and User Level, resetting tab to level default
  const handleLevelChange = (key: string) => {
    const newLevel = key as LevelType;
    setActiveLevel(newLevel);
    const defaultTab = REPORT_TABS_BY_LEVEL[newLevel][0];
    setActiveTab(defaultTab);
    navigate(`?report_type=${defaultTab}`, { replace: true });
  };

  // Compute CSS classes for tab buttons, highlighting the active tab
  const getTabClass = (tabName: TabType) => {
    const baseClass = styles["button-wrapper__button"];
    const activeClass = styles["button-wrapper__button--active"];

    return `${baseClass} ${activeTab === tabName ? activeClass : ""}`;
  };

  return (
    <div className={styles["page-container"]}>
      <LayoutHeader
        headerTitle="Reports"
        amount={dashboardData?.amount || 0}
        currency={dashboardData?.currency || "AED"}
        tabs={headerTabs}
        activeTabKey={activeLevel}
        onTabChange={handleLevelChange}
      />

      <div className={styles["wrapper"]}>
        <div className={styles["wrapper__head"]}>
          <div className={styles["button-wrapper"]}>
            {REPORT_TABS_BY_LEVEL[activeLevel].map((tab) => (
              <div
                key={tab}
                className={getTabClass(tab)}
                onClick={() => handleTabChange(tab)}
              >
                {TAB_LABELS[tab]}
              </div>
            ))}
          </div>
        </div>
        <div className={styles["wrapper__body"]}>
          {reportsLoading ? (
            <div className={styles["loader-container"]}>
              <CircularProgress />
            </div>
          ) : (
            <iframe
              className={styles["wrapper__iframe"]}
              src={data?.data?.qs_url}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Reports;
